import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Envelope } from '../types/graph-dialog.interface';

export interface EnvelopeValidationError {
  type: 'empty' | 'too_long' | 'duplicate';
  message: string;
}

@Injectable()
export class EnvelopeManager {
  private readonly translate = inject(TranslocoService);
  readonly envelopes: WritableSignal<Envelope[]> = signal([]);

  add(envelope: Envelope): { success: boolean; error?: EnvelopeValidationError } {
    const validation = this.validateName(envelope.name);
    if (!validation.valid) {
      return validation.error !== undefined ? { success: false, error: validation.error } : { success: false };
    }

    const newId = envelope.id === -1 ? this.generateId() : envelope.id;
    const envelopeWithId: Envelope = { ...envelope, id: newId };
    this.envelopes.update(curves => [...curves, envelopeWithId]);
    return { success: true };
  }

  private generateId(): number {
    const currentEnvelopes = this.envelopes();
    const negativeIds = currentEnvelopes.map(e => e.id).filter(id => id < 0);
    if (negativeIds.length === 0) return -1;
    return Math.min(...negativeIds) - 1;
  }

  update(id: number, formData: Envelope): { success: boolean; error?: EnvelopeValidationError } {
    const validation = this.validateName(formData.name, id);
    if (!validation.valid) {
      return validation.error !== undefined ? { success: false, error: validation.error } : { success: false };
    }

    const updatedEnvelope: Envelope = { ...formData, id };
    this.envelopes.update(curves => curves.map(c => (c.id === id ? updatedEnvelope : c)));
    return { success: true };
  }

  remove(id: number): void {
    this.envelopes.update(curves => curves.filter(c => c.id !== id));
  }

  clear(): void {
    this.envelopes.set([]);
  }

  getById(id: number): Envelope | undefined {
    return this.envelopes().find(e => e.id === id);
  }

  getTraceId(id: number): string {
    return 'env#' + id;
  }

  private validateName(name: string, excludeId?: number): { valid: boolean; error?: EnvelopeValidationError } {
    if (!name || name.trim().length === 0) {
      return {
        valid: false,
        error: { type: 'empty', message: this.translate.translate('graphs.envelope.validation.empty') },
      };
    }

    if (name.length > 100) {
      return {
        valid: false,
        error: { type: 'too_long', message: this.translate.translate('graphs.envelope.validation.tooLong') },
      };
    }

    const duplicate = this.envelopes().find(e => e.name === name && e.id !== excludeId);
    if (duplicate) {
      return {
        valid: false,
        error: { type: 'duplicate', message: this.translate.translate('graphs.envelope.validation.duplicate', { name }) },
      };
    }

    return { valid: true };
  }
}
